import { parseEntity } from '../entity/entity';
import { readPlane, refKey, withPlane } from '../plane/planeKey';
import type { PlanePlacement, PlaneRef } from '../plane/planeKey';
import { newUlid } from '../entity/catalog';
import { bestMatches } from '../palette/fuzzy';
import { arcEntryPath, heldRefs, withMemberLine } from '../nook/nook';
import { newArcFile } from '../nook/newArc';
import { isArchived, innerArcNames } from '../plan/arc';
import type { Arc, Status } from '../plan/arc';
import { readList, withList } from '../plan/edits';
import type { FileEdit } from '../plan/edits';
import type { CorpusItem } from '../thoughts/corpus';
import { nameOf, newNookFile, newNookPath, withNookName, withoutDraft } from '../workspaces/workspace';
import type { Workspace } from '../workspaces/workspace';
import type { CatalogNote } from '../entity/catalog';

/** Something that can be put into an arc or a nook: an arc, or a capture —
 *  a recording, a note, or one topic of a recording. */
export interface Item {
  path: string;
  anchor: string | null;
  kind: 'arc' | 'recording' | 'note';
  label: string;
}

export type HolderKind = 'arc' | 'nook';

export interface Holder {
  kind: HolderKind;
  path: string;
  raw: string;
}

const keyOf = (path: string, anchor: string | null) => (anchor ? `${path}#^${anchor}` : path);

/** What the holder already holds, as item keys. */
export function heldKeys(holder: Holder): Set<string> {
  const keys = new Set<string>();
  for (const entry of readList(holder.raw, 'arcs')) {
    const path = arcEntryPath(entry);
    if (path) keys.add(path);
  }
  if (holder.kind === 'arc') {
    for (const entry of readList(holder.raw, 'sources')) {
      const [path = '', anchor] = entry.split(' ^');
      keys.add(keyOf(path.trim(), anchor?.trim() ?? null));
    }
  } else {
    for (const ref of heldRefs(holder.raw)) keys.add(keyOf(ref.ref, ref.anchors[0] ?? null));
  }
  return keys;
}

/**
 * The holder's file with [items] added, and only its file: an arc goes into
 * `arcs:` (which is what makes a holder a storyline), a capture into an arc's
 * `sources:` or a nook's `## Members`. What it already holds is skipped; the
 * text comes back unchanged when nothing is new. Adding to a draft nook keeps
 * it, in the same text.
 */
export function withItemsAdded(holder: Holder, items: readonly Item[]): string {
  const held = heldKeys(holder);
  let text = holder.raw;
  const arcs: Item[] = [];
  const captures: Item[] = [];
  for (const item of items) {
    const key = keyOf(item.path, item.anchor);
    if (held.has(key) || key === holder.path) continue;
    held.add(key);
    (item.kind === 'arc' ? arcs : captures).push(item);
  }
  if (arcs.length > 0) {
    text = withList(text, 'arcs', [...readList(text, 'arcs'), ...arcs.map((a) => a.path)]);
  }
  if (holder.kind === 'arc') {
    if (captures.length > 0) {
      const entries = captures.map((c) => (c.anchor ? `${c.path} ^${c.anchor}` : c.path));
      text = withList(text, 'sources', [...readList(text, 'sources'), ...entries]);
    }
  } else {
    for (const c of captures) text = withMemberLine(text, c.anchor ? `${c.path}#^${c.anchor}` : c.path, c.label);
    if (text !== holder.raw) text = withoutDraft(text);
  }
  return text;
}

/**
 * A row dropped on a nook's canvas: its card at [at], and — for [item], null
 * for a nook, which is only ever placed — held, in the same text so no device
 * sees it on the canvas without the nook holding it. Dropping what is already
 * placed moves it, keeping its size and tint; what is already held stays as it
 * is. Dropped on its own canvas, a nook is left alone.
 */
export function withDropped(
  raw: string,
  nookPath: string,
  ref: PlaneRef,
  at: { x: number; y: number },
  item: Item | null,
): string {
  if (ref.ref === nookPath) return raw;
  const held = item ? withItemsAdded({ kind: 'nook', path: nookPath, raw }, [item]) : raw;
  const plane = readPlane(parseEntity(nookPath, held));
  const key = refKey(ref);
  const existing = plane.find((p) => refKey(p) === key);
  const placed: PlanePlacement = { ...existing, ...ref, x: Math.round(at.x), y: Math.round(at.y) };
  return withPlane(held, [...plane.filter((p) => refKey(p) !== key), placed]);
}

/** A wall card as something to add: a recording or a note, whole. Null for
 *  anything else the corpus holds. */
export function captureItem(c: CorpusItem, label: string | null): Item | null {
  if (c.kind !== 'recording' && c.kind !== 'note') return null;
  return { path: c.path, anchor: null, kind: c.kind, label: label ?? c.name.replace(/\.md$/, '') };
}

/** Every arc that holds [arc], however far up. */
function ancestorsOf(arc: Arc, all: readonly Arc[]): Set<string> {
  const out = new Set<string>();
  const walk = (name: string) => {
    for (const other of all) {
      if (!out.has(other.name) && innerArcNames(other).includes(name)) {
        out.add(other.name);
        walk(other.name);
      }
    }
  };
  walk(arc.name);
  return out;
}

export interface Candidate {
  key: string;
  item: Item;
}

/** Captures offered when nothing has been typed, and arcs: each a short run. */
const IDLE_EACH = 4;

/**
 * What can be added into [holder]: live arcs and captures, minus the holder
 * itself, what it already holds, and — for an arc — any arc that holds it,
 * which would close a loop.
 */
export function addCandidates({
  holder,
  arcs,
  corpus,
  nameFor,
  query,
  limit = 8,
}: {
  holder: Holder;
  arcs: readonly Arc[];
  corpus: readonly CorpusItem[];
  nameFor: (path: string) => string | null;
  query: string;
  limit?: number;
}): Candidate[] {
  const held = heldKeys(holder);
  const self = arcs.find((a) => a.path === holder.path);
  const above = self ? ancestorsOf(self, arcs) : new Set<string>();

  const arcItems: Candidate[] = arcs
    .filter((a) => !isArchived(a) && a.path !== holder.path && !held.has(a.path) && !above.has(a.name))
    .map((a) => ({ key: a.path, item: { path: a.path, anchor: null, kind: 'arc', label: a.title } }));

  const captures: Candidate[] = corpus
    .filter((c): c is CorpusItem & { kind: 'recording' | 'note' } => c.kind === 'recording' || c.kind === 'note')
    .filter((c) => c.path !== holder.path && !held.has(c.path))
    .map((c) => ({
      key: c.path,
      item: { path: c.path, anchor: null, kind: c.kind, label: nameFor(c.path) ?? c.name.replace(/\.md$/, '') },
    }));

  if (!query.trim()) return [...arcItems.slice(0, IDLE_EACH), ...captures.slice(0, IDLE_EACH)];
  return bestMatches([...arcItems, ...captures], query, (c) => c.item.label, limit);
}

export interface HolderCandidate {
  key: string;
  kind: HolderKind;
  label: string;
  arc?: Arc;
  nook?: Workspace;
}

/**
 * Where [items] can go: live arcs and live nooks matching [query]. An arc is
 * never offered for itself or for anything that sits above it; nor is a nook
 * held inside another, which is that nook's, nor a scratch canvas.
 */
export function holderCandidates({
  items,
  arcs,
  nooks,
  catalog,
  query,
  limit = 8,
}: {
  items: readonly Item[];
  arcs: readonly Arc[];
  nooks: readonly Workspace[];
  catalog: ReadonlyMap<string, CatalogNote>;
  query: string;
  limit?: number;
}): HolderCandidate[] {
  const moved = items.filter((i) => i.kind === 'arc').map((i) => i.path);
  const blocked = new Set<string>(moved);
  for (const a of arcs) {
    if (moved.some((p) => ancestorsOf(a, arcs).has(p.replace(/^.*\//, '')))) blocked.add(a.path);
  }
  const arcRows: HolderCandidate[] = arcs
    .filter((a) => !isArchived(a) && !blocked.has(a.path))
    .map((arc) => ({ key: arc.path, kind: 'arc', label: arc.title, arc }));
  const nookRows: HolderCandidate[] = nooks
    .filter((w) => !w.archived && !w.scratch && !w.inside)
    .map((nook) => ({ key: nook.path, kind: 'nook', label: nameOf(nook, catalog).name, nook }));
  const all = [...arcRows, ...nookRows];
  if (!query.trim()) return all.slice(0, limit);
  return bestMatches(all, query, (c) => c.label, limit);
}

/** Every holder that [items] could still be added to: [holderCandidates]
 *  without a query, minus the arcs that already hold all of them. */
export function holdersFor(
  items: readonly Item[],
  arcs: readonly Arc[],
  nooks: readonly Workspace[],
  catalog: ReadonlyMap<string, CatalogNote>,
): HolderCandidate[] {
  return holderCandidates({ items, arcs, nooks, catalog, query: '', limit: Infinity }).filter(
    (h) => !h.arc || withItemsAdded({ kind: 'arc', path: h.arc.path, raw: h.arc.raw }, items) !== h.arc.raw,
  );
}

/** The new holders offered for what was typed — none when it names one that
 *  exists exactly. */
export function createOffers(query: string, holders: readonly HolderCandidate[]): HolderKind[] {
  const typed = query.trim().toLowerCase();
  if (!typed) return [];
  return holders.some((h) => h.label.trim().toLowerCase() === typed) ? [] : ['arc', 'nook'];
}

/** A new arc holding [items], in one file: the arc's own creation path, with
 *  its `arcs:` and `sources:` already filled. */
export function newArcHolding(
  name: string,
  items: readonly Item[],
  id: string = newUlid(),
  status: Status = 'next',
): FileEdit | null {
  const captures = items.filter((i) => i.kind !== 'arc');
  const file = newArcFile(
    {
      name,
      description: '',
      sources: captures.map((c) => (c.anchor ? `${c.path} ^${c.anchor}` : c.path)),
      status,
      startWith: '',
      doneWhen: '',
    },
    id,
  );
  if (!file) return null;
  const arcs = items.filter((i) => i.kind === 'arc').map((i) => i.path);
  return { path: file.path, text: arcs.length > 0 ? withList(file.text, 'arcs', arcs) : file.text };
}

/** A new nook holding [items], named [name], in one file. */
export function newNookHolding(
  name: string,
  items: readonly Item[],
  taken: (path: string) => boolean,
  now: Date = new Date(),
): FileEdit {
  const path = newNookPath(now, taken);
  const raw = withNookName(newNookFile(newUlid(now.getTime()), now), name);
  return { path, text: withItemsAdded({ kind: 'nook', path, raw }, items) };
}

